import React, { useEffect, useMemo, useRef, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { approveDetailOrders, cancelOrder, deleteOrders, getDetailOrders, rejectDetailOrders, updateOrders } from '../../services/order.service';
import { getGroupProduct, getListProductWithOrderTemplate } from '../../services/items.services';
import { getDetailOrderTemplates } from '../../services/ordertemplates.service';
import { showDeleteToast, showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import SupplierReturnFlow from './SupplierReturnFlow';
import { orderFacts, useOrderPrint } from './orderShared';
import {
  Button,
  ConfirmDialog,
  DocumentHeader,
  EmptyState,
  ErrorBanner,
  FieldMessage,
  IconButton,
  LinesCard,
  Menu,
  MenuItem,
  NextStep,
  NumberField,
  ORDER_STATUS,
  PageFrame,
  PhoneLabel,
  PhoneLine,
  PickerRow,
  ProductCell,
  ProductPicker,
  ReadValue,
  SaveBar,
  SelectField,
  Skeleton,
  TotalsList,
  card,
  isCancellableStatus,
  money,
  quantity,
  tableStyle,
  td,
  th,
  useLanguage,
} from '../../components/purchasing';
import {
  Capacity,
  EditLine,
  ORDER_LINE_MSG,
  baseUnitOf,
  changesOf,
  checkLine,
  filledQty,
  hasDuplicateLines,
  labelOf,
  lineForPick,
  mergeTemplateLines,
  orderPayload,
  parseNumber,
  priceText,
  productOnTemplate,
  templateLevelsText,
  templateLineOfLine,
  templateLinesOfPicks,
  toEditLine,
  totalsOf,
  unitIdOrBase,
  unitNameOf,
  unitTaken,
  vatRateOf,
  withUnit,
} from './orderLineRules';

const PICK_PAGE = 20;
const SENT: number[] = [ORDER_STATUS.Approved, ORDER_STATUS.SentToSupplier, ORDER_STATUS.Acknowledged, ORDER_STATUS.PartiallyReceived];

type Mode = 'edit' | 'read' | 'delivery';

const unitsOf = (product: any): { id: string; name: string }[] =>
  (product?.productUOMs ?? []).filter((unit: any) => unit?.id).map((unit: any) => ({ id: String(unit.id), name: String(unit?.uom?.uomName ?? '') }));

/** One page for an order in every state: a draft is edited here, the rest is read with the step that comes next. */
const OrderDetail = () => {
  const preLanguage = localStorage.getItem('language');
  const companyId = localStorage.getItem('companyId') || '';
  const id: string = window.location.pathname.split('/').pop() || '';
  // Opened from the Deliveries list, which Back returns to.
  const fromDeliveries = new URLSearchParams(location.search).get('from') === 'deliveries';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [lines, setLines] = useState<EditLine[]>([]);
  const [saved, setSaved] = useState<EditLine[]>([]);
  const [flagged, setFlagged] = useState(false);
  const [notice, setNotice] = useState('');
  const [templateLines, setTemplateLines] = useState<any[]>([]);
  const [lineSearch, setLineSearch] = useState('');
  const [busy, setBusy] = useState<'' | 'draft' | 'send' | 'approve' | 'reject' | 'cancel' | 'delete'>('');
  const [confirm, setConfirm] = useState<'' | 'reject' | 'cancel' | 'delete' | 'leave'>('');
  const [returnOpen, setReturnOpen] = useState(false);

  // Add products
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [pickTerm, setPickTerm] = useState('');
  const [groupId, setGroupId] = useState('');
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [picks, setPicks] = useState<any[]>([]);
  const [pickCount, setPickCount] = useState(0);
  const [pickPage, setPickPage] = useState(1);
  const [picksLoading, setPicksLoading] = useState(false);
  const [selected, setSelected] = useState<Map<string, string>>(() => new Map());
  // Every product the picker has shown, for its supplier prices when a line changes unit.
  const seenPicks = useRef(new Map<string, any>());
  const pickSeq = useRef(0);

  const statusId = Number(order?.statusId);
  const mode: Mode = statusId === ORDER_STATUS.Draft ? 'edit' : SENT.includes(statusId) || statusId === ORDER_STATUS.Received ? 'delivery' : 'read';
  const editable = mode === 'edit';
  const supplier = order?.client?.companyName ?? '';
  const requiresApproval = !!order?.client?.requiresApproval;
  const canApprove = !!order?.viewer?.canApprove;
  const title = order ? t('Order #{number}', { number: order.internalNumber }) : t('Order');

  /* ---------------- Loading ---------------- */

  const apply = (raw: any) => {
    const next = (raw?.orderDetails ?? []).map(toEditLine);
    setOrder(raw);
    setLines(next);
    setSaved(next);
    setFlagged(false);
    setNotice('');
    setTemplateLines((prev) => mergeTemplateLines(prev, (raw?.orderDetails ?? []).map((detail: any) => detail.orderTemplateDetail)));
  };

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const response: any = await getDetailOrders(id);
      const raw = response?.data?.data;
      apply(raw);
      // A draft can change units, so it needs every line of its template; a sent order does not.
      if (Number(raw?.statusId) === ORDER_STATUS.Draft && raw?.orderTemplateId) {
        getDetailOrderTemplates(raw.orderTemplateId)
          .then((templateResponse: any) => setTemplateLines((prev) => mergeTemplateLines(prev, templateResponse?.data?.data?.details)))
          .catch((error: any) => console.error('Error loading template lines:', error));
      }
    } catch (error: any) {
      setFailed(true);
      showApiError(error, { language });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id === 'new') {
      // Orders are created from the overview's New order dialog.
      webNavigate(`/${preLanguage}/orderManagement?new=1`);
      return;
    }
    load();
    const onToken = () => load();
    document.addEventListener('newToken', onToken);
    return () => document.removeEventListener('newToken', onToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ---------------- Lines ---------------- */

  const savedByKey = useMemo(() => new Map(saved.map((line) => [line.key, line])), [saved]);
  // Levels in the line's own unit, also when the template counts the product in another unit.
  const templateLineOf = (line: EditLine) => templateLineOfLine(line, templateLines, lines);
  const checks = useMemo(
    () => new Map(lines.map((line) => [line.key, checkLine(line, templateLineOf(line), savedByKey.get(line.key), flagged)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, templateLines, savedByKey, flagged],
  );
  const changes = editable ? changesOf(lines, saved) : [];
  const dirty = changes.length > 0;
  const attention = editable ? lines.filter((line) => checks.get(line.key)?.blocking).length : 0;

  const term = lineSearch.trim().toLowerCase();
  const shown = term
    ? lines.filter((line) => `${line.product?.productName ?? ''} ${line.product?.sku ?? ''}`.toLowerCase().includes(term))
    : lines;

  const patch = (key: string, change: Partial<EditLine>) => {
    setNotice('');
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...change } : line)));
  };
  const supplierRowsOf = (line: EditLine) => line.product?.supplierInventory ?? seenPicks.current.get(line.productId)?.product?.supplierInventory;
  const changeUnit = (line: EditLine, productUOMId: string) => {
    // One line per product and unit: the other units of the product are disabled in the list.
    if (!productUOMId || productUOMId === line.productUOMId || unitTaken(lines, line.productId, productUOMId, line.key)) return;
    setLines((prev) => prev.map((other) => (other.key === line.key ? withUnit(other, productUOMId, order?.clientId, supplierRowsOf(other)) : other)));
  };
  const removeLine = (key: string) => setLines((prev) => prev.filter((line) => line.key !== key));
  const discard = () => {
    setLines(saved);
    setFlagged(false);
    setNotice('');
  };
  const fill = (capacity: Capacity) => setLines((prev) => prev.map((line) => ({ ...line, qty: filledQty(line, templateLineOf(line), capacity) })));

  /* ---------------- Values ---------------- */

  const receivedOf = (line: EditLine) => line.deliveryQty;
  const valueRows = lines.map((line) => {
    const rate = vatRateOf(line);
    if (editable) return { amount: checks.get(line.key)?.amount ?? null, rate };
    const price = parseNumber(line.price) ?? 0;
    const qty = statusId === ORDER_STATUS.Received ? receivedOf(line) : parseNumber(line.qty) ?? 0;
    return { amount: Number.isFinite(price * qty) ? price * qty : null, rate };
  });
  const totals = totalsOf(valueRows);
  const totalRows = [
    { label: t('Subtotal'), value: money(totals.subtotal) },
    { label: totals.rate !== null ? t('VAT {rate}%', { rate: quantity(totals.rate) }) : t('VAT'), value: money(totals.vat) },
    { label: t('Total'), value: money(totals.total), strong: true },
  ];

  /* ---------------- Actions ---------------- */

  const backToOrders = () => webNavigate(fromDeliveries ? `/${preLanguage}/deliveries` : `/${preLanguage}/orderManagement`);
  const onBack = () => (dirty ? setConfirm('leave') : backToOrders());

  const save = async (send: boolean) => {
    if (busy) return;
    if (lines.some((line) => checks.get(line.key)?.hard)) {
      setFlagged(true);
      return;
    }
    if (lines.some((line) => !line.productUOMId)) {
      setNotice(t('Choose an order unit for every product'));
      return;
    }
    if (hasDuplicateLines(lines)) {
      setNotice(t(ORDER_LINE_MSG.unitTaken));
      return;
    }
    setBusy(send ? 'send' : 'draft');
    try {
      await updateOrders(id, orderPayload(order, lines, send ? ORDER_STATUS.SentForApproval : ORDER_STATUS.Draft, companyId) as any);
      showToast({
        message: !send ? t('Draft saved') : requiresApproval ? t('Order sent for approval') : t('Order sent to {supplier}', { supplier }),
        type: 'success',
        duration: 5000,
      });
      await load();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  const approve = async () => {
    if (busy) return;
    setBusy('approve');
    try {
      await approveDetailOrders(id);
      showToast({ message: t('Order approved and sent to {supplier}', { supplier }), type: 'success', duration: 5000 });
      await load();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  const reject = async () => {
    setBusy('reject');
    try {
      await rejectDetailOrders(id);
      setConfirm('');
      showToast({ message: t('Order rejected'), type: 'success', duration: 5000 });
      await load();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  const cancel = async () => {
    if (busy) return;
    setBusy('cancel');
    try {
      await cancelOrder(id);
      setConfirm('');
      showToast({ message: t('Order cancelled'), type: 'success', duration: 5000 });
      await load();
    } catch (error: any) {
      // Refused (say part of it was received meanwhile): close and show the order as it is now.
      setConfirm('');
      showApiError(error, { language });
      await load();
    } finally {
      setBusy('');
    }
  };

  const removeDraft = async () => {
    setBusy('delete');
    try {
      await deleteOrders(id);
      setConfirm('');
      showDeleteToast(t('Order Deleted !'));
      backToOrders();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  /* ---------------- Add products ---------------- */

  useDebounceEffect(() => setPickTerm(pickSearch.trim()), [pickSearch], 300);

  const loadPicks = async (page: number, append: boolean) => {
    const mine = ++pickSeq.current;
    setPicksLoading(true);
    try {
      const response: any = await getListProductWithOrderTemplate(page, PICK_PAGE, pickTerm, order?.locationId ?? '', id, groupId || undefined);
      if (mine !== pickSeq.current) return;
      const rows: any[] = response?.data?.data?.orderTemplateDetails ?? [];
      rows.forEach((row) => seenPicks.current.set(String(row.productId), row));
      setTemplateLines((prev) => mergeTemplateLines(prev, templateLinesOfPicks(rows)));
      setPicks((prev) => {
        if (!append) return rows;
        const known = new Set(prev.map((row) => String(row.productId)));
        return [...prev, ...rows.filter((row) => !known.has(String(row.productId)))];
      });
      setPickCount(Number(response?.data?.data?.count) || 0);
      setPickPage(page);
    } catch (error: any) {
      if (mine === pickSeq.current) showApiError(error, { language });
    } finally {
      if (mine === pickSeq.current) setPicksLoading(false);
    }
  };

  useEffect(() => {
    if (pickerOpen) loadPicks(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, pickTerm, groupId]);

  const openPicker = () => {
    setPickSearch('');
    setPickTerm('');
    setGroupId('');
    setSelected(new Map());
    setPickerOpen(true);
    if (!groups.length) {
      getGroupProduct(companyId, 1, 1000, '')
        .then((response: any) => {
          const rows: any[] = response?.data?.productGroupWithCompany ?? response?.data?.data?.productGroupWithCompany ?? [];
          setGroups(rows.filter((row) => row?.id).map((row) => ({ id: String(row.id), name: String(row.groupName ?? '') })));
        })
        // Without groups the picker only loses its group chips.
        .catch(() => undefined);
    }
  };

  // A product starts on its first template unit that is not on the order yet, else its base unit, else any free unit.
  const defaultUnitOf = (pick: any): string => {
    const units = pick?.product?.productUOMs ?? [];
    const free = (unitId: string) => !!unitId && !unitTaken(lines, pick?.productId, unitId);
    const templateUnits = (pick?.orderTemplateDetails ?? []).map((line: any) => unitIdOrBase(line?.productUOMId, units));
    return templateUnits.find(free) ?? (free(baseUnitOf(units)?.id ?? '') ? String(baseUnitOf(units).id) : unitsOf(pick?.product).map((unit) => unit.id).find(free) ?? '');
  };

  const pickerRows: PickerRow[] = picks.map((pick) => {
    const productId = String(pick.productId);
    return {
      id: productId,
      name: pick?.product?.productName ?? '',
      sku: pick?.product?.sku ?? null,
      group: pick?.product?.productGroup?.groupName ?? null,
      units: unitsOf(pick?.product).map((unit) => ({ ...unit, taken: unitTaken(lines, productId, unit.id) })),
      selected: selected.has(productId),
      unitId: selected.get(productId) ?? defaultUnitOf(pick),
    };
  });

  const togglePick = (row: PickerRow) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else if (row.unitId) next.set(row.id, row.unitId);
      return next;
    });
  const pickUnit = (row: PickerRow, unitId: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(row.id, unitId);
      return next;
    });

  const addPicked = () => {
    const keys = new Set(lines.map((line) => line.key));
    const added: EditLine[] = [];
    selected.forEach((unitId, productId) => {
      const pick = seenPicks.current.get(productId);
      if (!pick || !unitId || unitTaken([...lines, ...added], productId, unitId)) return;
      const line = lineForPick(pick, unitId, order?.clientId, keys, [...lines, ...added]);
      keys.add(line.key);
      added.push(line);
    });
    setLines((prev) => [...prev, ...added]);
    // A just-added line is never hidden by the line search.
    setLineSearch('');
    setPickerOpen(false);
  };

  /* ---------------- Header ---------------- */

  const openLines = lines.filter((line) => receivedOf(line) < (parseNumber(line.qty) ?? 0)).length;
  // The receiving page lists each receipt with its note, so only the order page adds them to Received.
  const facts = orderFacts(order, t, { deliveryNotes: true });

  const receiveButton = (
    <Button variant="primary" phone={phone} fullWidth={phone} icon="truck" onClick={() => webNavigate(`/${preLanguage}/deliveries/${id}${fromDeliveries ? '?from=deliveries' : ''}`)}>
      {t('Receive delivery')}
    </Button>
  );
  let nextStep: NextStep | null = null;
  if (statusId === ORDER_STATUS.SentForApproval) {
    nextStep = canApprove
      ? {
          tone: 'waiting',
          text: t('Waiting for your approval. Approving sends the order to {supplier}.', { supplier }),
          actions: (
            <>
              {!phone && (
                <Button variant="danger" disabled={!!busy} onClick={() => setConfirm('reject')}>
                  {t('Reject order')}
                </Button>
              )}
              <Button variant="primary" phone={phone} fullWidth={phone} disabled={!!busy} aria-busy={busy === 'approve' || undefined} onClick={approve}>
                {busy === 'approve' ? t('Approving…') : t('Approve and send')}
              </Button>
              {phone && (
                <Button variant="danger" phone fullWidth disabled={!!busy} onClick={() => setConfirm('reject')}>
                  {t('Reject order')}
                </Button>
              )}
            </>
          ),
        }
      : { tone: 'waiting', text: t('Waiting for a company admin to approve') };
  } else if (statusId === ORDER_STATUS.PartiallyReceived) {
    nextStep = {
      tone: 'progress',
      text: t('{open} of {count} lines are still open. Receive the rest when it arrives.', { open: openLines, count: lines.length }),
      actions: receiveButton,
    };
  } else if (SENT.includes(statusId)) {
    nextStep = { tone: 'progress', text: t('Sent to {supplier}. Receive the delivery when it arrives.', { supplier }), actions: receiveButton };
  } else if (statusId === ORDER_STATUS.Cancelled) {
    nextStep = { tone: 'stopped', text: t('This order was cancelled. Nothing was received.') };
  }

  const moreItems: MenuItem[] = [
    ...(editable && phone && dirty ? [{ key: 'discard', label: t('Discard changes'), icon: 'undo', onSelect: discard }] : []),
    // The return picker is a desktop dialog, as before.
    ...(statusId === ORDER_STATUS.Received && !phone ? [{ key: 'return', label: t('Return to supplier'), icon: 'undo', onSelect: () => setReturnOpen(true) }] : []),
    ...(isCancellableStatus(statusId) ? [{ key: 'cancel', label: t('Cancel order'), icon: 'close', danger: true, disabled: !!busy, onSelect: () => setConfirm('cancel') }] : []),
    ...(editable ? [{ key: 'delete', label: t('Delete draft'), icon: 'trash', danger: true, onSelect: () => setConfirm('delete') }] : []),
  ];

  /* ---------------- Lines card ---------------- */

  const fillMenu = editable && lines.length > 0 && (
    <Menu
      label={t('Fill quantities')}
      width={260}
      items={[
        { key: 'low', label: t('Minimum'), desc: t('Reorder level from the template'), onSelect: () => fill('low') },
        { key: 'medium', label: t('Usual'), desc: t('Order qty from the template'), onSelect: () => fill('medium') },
        { key: 'max', label: t('Maximum'), desc: t('Max per order from the template'), onSelect: () => fill('max') },
      ]}
      trigger={(props) => (
        <Button phone={phone} trailingIcon="chevronDown" {...props}>
          {t('Fill quantities')}
        </Button>
      )}
    />
  );
  const addButton = editable && lines.length > 0 && (
    <Button phone={phone} icon="plus" onClick={openPicker}>
      {t('Add products')}
    </Button>
  );

  const contextOf = (line: EditLine): string => {
    if (mode === 'delivery') {
      const open = Math.max(0, (parseNumber(line.qty) ?? 0) - receivedOf(line));
      if (open <= 0) return t('All received');
      // A received order is closed: what did not come is short, not open.
      return statusId === ORDER_STATUS.Received ? t('{qty} short', { qty: quantity(open) }) : t('{qty} still open', { qty: quantity(open) });
    }
    if (!editable) return line.product?.productGroup?.groupName ?? '';
    const base = baseUnitOf(line.product?.productUOMs);
    const baseName = base && base.id !== line.productUOMId ? base?.uom?.uomName : '';
    const onHand = t('On hand {qty}', { qty: `${quantity(line.currentStock)}${baseName ? ` ${baseName}` : ''}` });
    if (phone) return onHand;
    const templateLine = templateLineOf(line);
    // The product's template line can be another line's (the one in its unit): then this line just shows its stock.
    if (!templateLine) return productOnTemplate(line, templateLines) ? onHand : `${onHand} · ${t('Not on the template')}`;
    // A line in another unit shows the template's own levels in the template's unit.
    const own = templateLevelsText(line, templateLine);
    if (own) {
      const unitText = own.unit ? ` ${own.unit}` : '';
      const ownMax = own.max ? ` · ${t('Max {qty}', { qty: `${own.max}${unitText}` })}` : '';
      return `${onHand} · ${t('Reorder at {qty}', { qty: `${own.low}${unitText}` })}${ownMax}`;
    }
    // A Max of 0 on the template means no limit.
    const max = Number(templateLine.highQty) > 0 ? ` · ${t('Max {qty}', { qty: quantity(templateLine.highQty) })}` : '';
    return `${onHand} · ${t('Reorder at {qty}', { qty: quantity(templateLine.lowQty) })}${max}`;
  };

  const qtyMessage = (check: any) => (check.qtyError === 'Max {max}' ? t('Max {max}', { max: quantity(check.qtyMax) }) : t(check.qtyError));
  const unitSelect = (line: EditLine, selectId: string) => (
    <SelectField id={selectId} phone={phone} value={line.productUOMId} onChange={(event) => changeUnit(line, event.target.value)} wrapperStyle={phone ? { flex: 'none', width: '116px' } : undefined}>
      {!line.productUOMId && <option value="">{t('Choose a unit')}</option>}
      {unitsOf(line.product).map((unit) => {
        const taken = unit.id !== line.productUOMId && unitTaken(lines, line.productId, unit.id, line.key);
        return (
          <option key={unit.id} value={unit.id} disabled={taken}>
            {taken ? t('{unit} · on order', { unit: unit.name }) : unit.name}
          </option>
        );
      })}
    </SelectField>
  );

  const priceNote = (check: any) => (check.priceError ? t(check.priceError) : check.priceWas !== null ? t('Was {price}', { price: money(check.priceWas) }) : '');

  const desktopTable = () => {
    if (mode === 'edit') {
      return (
        <div data-lines="" style={{ overflowX: 'auto' }}>
          <table style={tableStyle(720)}>
            <thead>
              <tr>
                <th scope="col" style={th('left', { first: true })}>
                  {t('Product')}
                </th>
                <th scope="col" style={th('left', { width: 124 })}>
                  {t('Order unit')}
                </th>
                <th scope="col" style={th('right', { width: 104 })}>
                  {t('Qty')}
                </th>
                <th scope="col" style={th('right', { width: 112 })}>
                  {t('Unit price')}
                </th>
                <th scope="col" style={th('right', { width: 90, padRight: 0 })}>
                  {t('Total')}
                </th>
                <th scope="col" style={th('left', { width: 44, padLeft: 2, padRight: 10 })}>
                  <span className="pu-sr">{t('Remove')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((line) => {
                const check: any = checks.get(line.key);
                const label = labelOf(line, lines);
                const error = check.blocking;
                const note = priceNote(check);
                return (
                  <tr key={line.key} className="pu-row">
                    <td style={td({ first: true, error })}>
                      <ProductCell name={line.product?.productName ?? ''} sku={line.product?.sku} context={contextOf(line)} />
                    </td>
                    <td style={td({ error })}>
                      <label className="pu-sr" htmlFor={`unit-${line.key}`}>
                        {t('Order unit for {product}', { product: label })}
                      </label>
                      {unitSelect(line, `unit-${line.key}`)}
                    </td>
                    <td style={td({ align: 'right', error })}>
                      <NumberField
                        id={`qty-${line.key}`}
                        column="qty"
                        srLabel={t('Qty for {product}', { product: label })}
                        value={line.qty}
                        invalid={!!check.qtyError}
                        describedBy={check.qtyError ? `qty-${line.key}-msg` : undefined}
                        onChange={(value) => patch(line.key, { qty: value })}
                      />
                      {check.qtyError && (
                        <FieldMessage id={`qty-${line.key}-msg`} tone="error">
                          {qtyMessage(check)}
                        </FieldMessage>
                      )}
                    </td>
                    <td style={td({ align: 'right', error })}>
                      <NumberField
                        id={`price-${line.key}`}
                        column="price"
                        prefix="€"
                        srLabel={t('Unit price for {product}', { product: label })}
                        value={line.price}
                        invalid={!!check.priceError}
                        describedBy={note ? `price-${line.key}-msg` : undefined}
                        onChange={(value) => patch(line.key, { price: value })}
                        onBlur={() => {
                          const n = parseNumber(line.price);
                          if (n !== null && Number.isFinite(n)) patch(line.key, { price: priceText(n) });
                        }}
                      />
                      {note && (
                        <FieldMessage id={`price-${line.key}-msg`} tone={check.priceError ? 'error' : 'notice'}>
                          {note}
                        </FieldMessage>
                      )}
                    </td>
                    <td style={td({ align: 'right', padRight: 0, error })}>
                      <ReadValue strong={check.amount !== null} muted={check.amount === null}>
                        {check.amount === null ? '—' : money(check.amount)}
                      </ReadValue>
                    </td>
                    <td style={td({ align: 'right', padLeft: 2, padRight: 10, padTop: 16, error })}>
                      <IconButton icon="close" remove label={t('Remove {product}', { product: label })} onClick={() => removeLine(line.key)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    const delivery = mode === 'delivery';
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle(delivery ? 760 : 680)}>
          <thead>
            <tr>
              <th scope="col" style={th('left', { first: true })}>
                {t('Product')}
              </th>
              <th scope="col" style={th('left', { width: delivery ? 104 : 124 })}>
                {t('Order unit')}
              </th>
              <th scope="col" style={th('right', { width: delivery ? 88 : 104 })}>
                {delivery ? t('Ordered') : t('Qty')}
              </th>
              {delivery && (
                <th scope="col" style={th('right', { width: 96 })}>
                  {t('Received')}
                </th>
              )}
              <th scope="col" style={th('right', { width: 112 })}>
                {t('Unit price')}
              </th>
              <th scope="col" style={th('right', { width: 110, last: true })}>
                {t('Total')}
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((line) => {
              const index = lines.indexOf(line);
              const amount = valueRows[index]?.amount ?? null;
              const received = receivedOf(line);
              return (
                <tr key={line.key} className="pu-row">
                  <td style={td({ first: true })}>
                    <ProductCell name={line.product?.productName ?? ''} sku={line.product?.sku} context={contextOf(line)} />
                  </td>
                  <td style={td()}>
                    <ReadValue>{unitNameOf(line) || '—'}</ReadValue>
                  </td>
                  <td style={td({ align: 'right' })}>
                    <ReadValue>{quantity(parseNumber(line.qty) ?? 0)}</ReadValue>
                  </td>
                  {delivery && (
                    <td style={td({ align: 'right' })}>
                      <ReadValue muted={received === 0}>{quantity(received)}</ReadValue>
                    </td>
                  )}
                  <td style={td({ align: 'right' })}>
                    <ReadValue>{money(parseNumber(line.price))}</ReadValue>
                  </td>
                  <td style={td({ align: 'right', last: true })}>
                    <ReadValue strong>{amount === null ? '—' : money(amount)}</ReadValue>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const phoneLines = () => (
    <div data-lines="">
      {shown.map((line) => {
        const check: any = checks.get(line.key);
        const label = labelOf(line, lines);
        const nameId = `line-${line.key}`;
        const index = lines.indexOf(line);
        if (!editable) {
          const delivery = mode === 'delivery';
          const amount = valueRows[index]?.amount ?? null;
          return (
            <PhoneLine key={line.key} labelledBy={nameId}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <ProductCell phone nameId={nameId} name={line.product?.productName ?? ''} sku={line.product?.sku} context={contextOf(line)} />
                <p style={{ flex: 'none', fontSize: '15px', lineHeight: '22px', color: '#222525' }}>
                  <span className="pu-sr">{t('Order unit')}: </span>
                  {unitNameOf(line)}
                </p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '10px', textAlign: 'right' }}>
                <div>
                  <PhoneLabel align="right">{delivery ? t('Ordered') : t('Qty')}</PhoneLabel>
                  <ReadValue phone>{quantity(parseNumber(line.qty) ?? 0)}</ReadValue>
                </div>
                <div>
                  <PhoneLabel align="right">{delivery ? t('Received') : t('Unit price')}</PhoneLabel>
                  <ReadValue phone>{delivery ? quantity(receivedOf(line)) : money(parseNumber(line.price))}</ReadValue>
                </div>
                <div>
                  <PhoneLabel align="right">{t('Total')}</PhoneLabel>
                  <ReadValue phone strong>
                    {amount === null ? '—' : money(amount)}
                  </ReadValue>
                </div>
              </div>
            </PhoneLine>
          );
        }
        const note = priceNote(check);
        return (
          <PhoneLine key={line.key} labelledBy={nameId} error={check.blocking}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <ProductCell phone nameId={nameId} name={line.product?.productName ?? ''} sku={line.product?.sku} context={contextOf(line)} />
              <label className="pu-sr" htmlFor={`m-unit-${line.key}`}>
                {t('Order unit for {product}', { product: label })}
              </label>
              {unitSelect(line, `m-unit-${line.key}`)}
              <IconButton icon="close" remove phone label={t('Remove {product}', { product: label })} onClick={() => removeLine(line.key)} style={{ marginRight: '-16px' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '10px' }}>
              <div style={{ minWidth: 0 }}>
                <PhoneLabel htmlFor={`m-qty-${line.key}`}>{t('Qty')}</PhoneLabel>
                <NumberField
                  id={`m-qty-${line.key}`}
                  column="qty"
                  phone
                  value={line.qty}
                  invalid={!!check.qtyError}
                  describedBy={check.qtyError ? `m-qty-${line.key}-msg` : undefined}
                  onChange={(value) => patch(line.key, { qty: value })}
                />
                {check.qtyError && (
                  <FieldMessage id={`m-qty-${line.key}-msg`} tone="error">
                    {qtyMessage(check)}
                  </FieldMessage>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <PhoneLabel htmlFor={`m-price-${line.key}`}>{t('Unit price')}</PhoneLabel>
                <NumberField
                  id={`m-price-${line.key}`}
                  column="price"
                  phone
                  prefix="€"
                  value={line.price}
                  invalid={!!check.priceError}
                  describedBy={note ? `m-price-${line.key}-msg` : undefined}
                  onChange={(value) => patch(line.key, { price: value })}
                  onBlur={() => {
                    const n = parseNumber(line.price);
                    if (n !== null && Number.isFinite(n)) patch(line.key, { price: priceText(n) });
                  }}
                />
                {note && (
                  <FieldMessage id={`m-price-${line.key}-msg`} tone={check.priceError ? 'error' : 'notice'}>
                    {note}
                  </FieldMessage>
                )}
              </div>
              <div style={{ minWidth: 0, textAlign: 'right' }}>
                <PhoneLabel align="right">{t('Total')}</PhoneLabel>
                <ReadValue phone strong={check.amount !== null} muted={check.amount === null}>
                  {check.amount === null ? '—' : money(check.amount)}
                </ReadValue>
              </div>
            </div>
          </PhoneLine>
        );
      })}
    </div>
  );

  const linesBody = () => {
    if (loading && !order) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: phone ? '16px' : '20px' }}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      );
    }
    if (lines.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          {statusId === ORDER_STATUS.Rejected ? (
            <EmptyState phone={phone} title={t('A rejected order keeps no products')} desc={t('Start a new order from the template to order again.')} />
          ) : editable ? (
            <EmptyState
              phone={phone}
              title={t('No products on this order yet')}
              desc={t('Use Add products to choose what to order.')}
              action={
                <Button variant="primary" phone={phone} icon="plus" onClick={openPicker}>
                  {t('Add products')}
                </Button>
              }
            />
          ) : (
            <EmptyState phone={phone} title={t('No products on this order')} />
          )}
        </div>
      );
    }
    if (shown.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          <EmptyState
            icon="search"
            phone={phone}
            title={t('No products on this order match "{search}". Check the spelling or search by SKU.', { search: lineSearch.trim() })}
            action={
              <Button variant="text" phone={phone} onClick={() => setLineSearch('')}>
                {t('Clear search')}
              </Button>
            }
          />
        </div>
      );
    }
    return phone ? phoneLines() : desktopTable();
  };

  /* ---------------- Save bar ---------------- */

  const changeText = (change: { kind: string; key: string }) => {
    const line = (change.kind === 'removed' ? saved : lines).find((other) => other.key === change.key);
    const name = line ? labelOf(line, change.kind === 'removed' ? saved : lines) : '';
    return change.kind === 'added' ? t('Added {name}', { name }) : change.kind === 'removed' ? t('Removed {name}', { name }) : t('Edited {name}', { name });
  };
  const summary = {
    lead: dirty ? (changes.length === 1 ? t('1 unsaved change') : t('{count} unsaved changes', { count: changes.length })) : t('No unsaved changes'),
    detail: dirty ? [changes.length > 1 ? t('{first}, +{count} more', { first: changeText(changes[0]), count: changes.length - 1 }) : changeText(changes[0])] : [],
    attention: notice || (attention ? (attention === 1 ? t('1 line needs attention') : t('{count} lines need attention', { count: attention })) : null),
    quiet: !dirty,
  };
  const sendLabel = requiresApproval ? t('Send for approval') : t('Send to supplier');
  const saveBar = editable && order && (
    <SaveBar
      phone={phone}
      label={t('Unsaved changes')}
      summary={summary}
      secondary={
        phone ? (
          <Button variant="text" phone disabled={!dirty || !!busy} onClick={() => save(false)}>
            {busy === 'draft' ? t('Saving…') : t('Save draft')}
          </Button>
        ) : (
          <>
            <Button variant="text" disabled={!dirty || !!busy} onClick={discard}>
              {t('Discard changes')}
            </Button>
            <Button disabled={!dirty || !!busy} aria-busy={busy === 'draft' || undefined} onClick={() => save(false)}>
              {busy === 'draft' ? t('Saving…') : t('Save draft')}
            </Button>
          </>
        )
      }
      primary={
        <Button variant="primary" phone={phone} fullWidth={phone} disabled={lines.length === 0 || !!busy} aria-busy={busy === 'send' || undefined} onClick={() => save(true)}>
          {busy === 'send' ? t('Sending…') : sendLabel}
        </Button>
      }
    />
  );

  const printDoc = useOrderPrint(
    order,
    saved.map((line) => {
      const qty = parseNumber(line.qty) ?? 0;
      const cost = parseNumber(line.price) ?? 0;
      return { sku: line.product?.sku, productName: line.product?.productName, uomName: unitNameOf(line), orderQty: qty, cost, lineCost: cost * qty };
    }),
  );

  return (
    <>
      {printDoc.node}
      <PageFrame phone={phone} withPhoneBar={editable}>
        {failed && !order ? (
          <ErrorBanner message={t('The order could not be loaded.')} retryLabel={t('Try again')} onRetry={load} />
        ) : (
          <>
            {order ? (
              <DocumentHeader
                phone={phone}
                t={t}
                backLabel={fromDeliveries ? t('Back to deliveries') : t('Back to orders')}
                onBack={onBack}
                title={title}
                statusId={statusId}
                nextStep={nextStep}
                facts={facts}
                onPrint={printDoc.print}
                onDownload={printDoc.download}
                moreItems={moreItems}
              />
            ) : (
              <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: '16px', padding: phone ? '16px' : '20px' }}>
                <Skeleton width="120px" />
                <Skeleton width="220px" height={28} />
                <Skeleton height={96} />
              </section>
            )}
            <LinesCard
              phone={phone}
              titleId="pu-lines-title"
              title={t('Products')}
              count={order ? lines.length : undefined}
              search={lines.length > 0 ? { label: t('Find a product in this order'), placeholder: t('Find a product in this order'), value: lineSearch, onChange: setLineSearch } : undefined}
              actions={
                fillMenu || addButton ? (
                  <>
                    {fillMenu}
                    {addButton}
                  </>
                ) : undefined
              }
              footer={lines.length > 0 && <TotalsList phone={phone} rows={totalRows} />}
            >
              {linesBody()}
            </LinesCard>
            {saveBar}
          </>
        )}
      </PageFrame>
      {editable && (
        <ProductPicker
          open={pickerOpen}
          phone={phone}
          t={t}
          title={t('Add products to {order}', { order: title })}
          search={pickSearch}
          onSearch={setPickSearch}
          groups={groups}
          groupId={groupId}
          onGroup={setGroupId}
          rows={pickerRows}
          loading={picksLoading}
          hasMore={picks.length < pickCount}
          onMore={() => loadPicks(pickPage + 1, true)}
          onToggle={togglePick}
          onUnit={pickUnit}
          selectedCount={selected.size}
          onCancel={() => setPickerOpen(false)}
          onAdd={addPicked}
        />
      )}
      {statusId === ORDER_STATUS.Received && <SupplierReturnFlow open={returnOpen} onClose={() => setReturnOpen(false)} order={order} language={language} />}
      <ConfirmDialog
        open={confirm === 'reject'}
        phone={phone}
        danger
        busy={busy === 'reject'}
        title={t('Reject order {number}?', { number: `#${order?.internalNumber ?? ''}` })}
        body={t('The order is marked rejected and its products are removed. It is not sent to {supplier}.', { supplier })}
        confirmLabel={t('Reject order')}
        cancelLabel={t('Cancel')}
        closeLabel={t('Close')}
        onConfirm={reject}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'cancel'}
        phone={phone}
        danger
        busy={busy === 'cancel'}
        title={t('Cancel order {number}?', { number: `#${order?.internalNumber ?? ''}` })}
        body={
          statusId === ORDER_STATUS.SentForApproval
            ? t('The order is marked cancelled and can no longer be approved, sent or received. This cannot be undone.')
            : t('The order is marked cancelled and can no longer be sent or received. Tell {supplier} if it was already sent. This cannot be undone.', { supplier })
        }
        confirmLabel={t('Cancel order')}
        cancelLabel={t('Keep order')}
        closeLabel={t('Close')}
        onConfirm={cancel}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        phone={phone}
        danger
        busy={busy === 'delete'}
        title={t('Delete draft order {number}?', { number: `#${order?.internalNumber ?? ''}` })}
        body={t('The draft and its products are removed. This cannot be undone.')}
        confirmLabel={t('Delete draft')}
        cancelLabel={t('Keep draft')}
        closeLabel={t('Close')}
        onConfirm={removeDraft}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'leave'}
        phone={phone}
        danger
        title={t('Leave without saving?')}
        body={t('Your changes to this order are not saved.')}
        confirmLabel={t('Leave without saving')}
        cancelLabel={t('Keep editing')}
        closeLabel={t('Close')}
        onConfirm={() => {
          setConfirm('');
          backToOrders();
        }}
        onCancel={() => setConfirm('')}
      />
    </>
  );
};

export default OrderDetail;
